<script setup lang="ts">
/**
 * OpenDocumentMenu (MYAC-1640): the popup that opens a cited document in the
 * system it came from ("Open in SharePoint"), or says why it cannot.
 *
 * Any markup raises it with `toggle(event, handle)`: a chip (`DocRef`), a
 * table row's button. A list mounts ONE for all its rows, as `GapClauseList`
 * does for its clause menu.
 *
 * The document and its datasource are read when the menu opens, not when the
 * citation renders: a page citing fifty documents reads nothing until someone
 * asks for one. An outcome is kept for the component's life, so reopening reads
 * nothing; a failed read is the exception and is retried on the next open.
 */
import { computed, ref, shallowReactive } from 'vue'
import { useI18n } from 'vue-i18n'
import Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'
import type { DocumentHandle } from '@/models/DocumentHandle'
import { useDatasourcesStore } from '@/stores/datasources'
import { useDocumentsStore } from '@/stores/documents'
import { buildSourceLink, isDocumentGone, isManualUpload, type SourceLink } from '@/utils/sourceLink'

type Outcome =
  | { state: 'loading' }
  | { state: 'link'; link: SourceLink }
  | { state: 'gone' }
  | { state: 'unreadable' }
  | { state: 'no_link' }
  | { state: 'manual_upload' }

const { t } = useI18n()
const documentsStore = useDocumentsStore()
const datasourcesStore = useDatasourcesStore()

const menu = ref<{ toggle: (event: Event, target?: HTMLElement) => void } | null>(null)
const outcomes = shallowReactive(new Map<string, Outcome>())
const currentKey = ref<string | null>(null)

function keyOf(handle: DocumentHandle): string {
  return `${handle.project_id}/${handle.datasource_id}/${handle.document_id}`
}

async function resolve(handle: DocumentHandle, key: string): Promise<void> {
  outcomes.set(key, { state: 'loading' })
  const [documentRead, datasourceRead] = await Promise.allSettled([
    documentsStore.getDocument(handle.project_id, handle.datasource_id, handle.document_id),
    datasourcesStore.getDatasource(handle.project_id, handle.datasource_id),
  ])
  if (documentRead.status === 'rejected') {
    outcomes.set(key, { state: 'unreadable' })
    return
  }
  if (isDocumentGone(documentRead.value)) {
    outcomes.set(key, { state: 'gone' })
    return
  }
  // A Drive link needs nothing from the datasource, so its failed read only
  // matters when no link could be built without it.
  const datasource = datasourceRead.status === 'fulfilled' ? datasourceRead.value : null
  const link = buildSourceLink(documentRead.value, datasource)
  if (link) outcomes.set(key, { state: 'link', link })
  else if (isManualUpload(documentRead.value, datasource)) outcomes.set(key, { state: 'manual_upload' })
  else outcomes.set(key, { state: datasourceRead.status === 'rejected' ? 'unreadable' : 'no_link' })
}

function disabledItem(labelKey: string, icon: string): MenuItem {
  return { label: t(labelKey), icon, disabled: true }
}

const items = computed<MenuItem[]>(() => {
  const outcome = currentKey.value ? outcomes.get(currentKey.value) : undefined
  switch (outcome?.state) {
    case 'link': {
      const { url, labelKey } = outcome.link
      return [{
        label: t(labelKey),
        icon: 'pi pi-external-link',
        command: () => void window.open(url, '_blank', 'noopener,noreferrer'),
      }]
    }
    case 'gone':
      return [disabledItem('document_source.gone', 'pi pi-ban')]
    case 'unreadable':
      return [disabledItem('document_source.unreadable', 'pi pi-exclamation-triangle')]
    case 'no_link':
      return [disabledItem('document_source.no_link', 'pi pi-info-circle')]
    case 'manual_upload':
      return [disabledItem('document_source.manual_upload', 'pi pi-upload')]
    default:
      return [disabledItem('document_source.loading', 'pi pi-spin pi-spinner')]
  }
})

/**
 * `target` anchors the popup when the caller toggles after the click has
 * finished dispatching, by which time `event.currentTarget` is null.
 */
function toggle(event: Event, handle: DocumentHandle, target?: HTMLElement): void {
  const key = keyOf(handle)
  currentKey.value = key
  const known = outcomes.get(key)
  if (!known || known.state === 'unreadable') void resolve(handle, key)
  menu.value?.toggle(event, target)
}

defineExpose({ toggle })
</script>

<template>
  <Menu ref="menu" :model="items" popup data-testid="open-document-menu" />
</template>
